import { useEffect, useMemo, useState } from "react";
import { Modal, Checkbox, Radio, Skeleton } from "antd";
import { Icon } from "@iconify/react";
import { Spinner } from "@/shared/components/spinner";
import { toastError } from "@/shared/lib/toast";
import { Button } from "@/shared/components/button";
import { EmptyState } from "@/shared/components/EmptyState";
import { Input } from "@/shared/components/dialog";
import {
  listEpicOrganizations,
  getEpicAuthorizeUrl,
  getCachedEpicOrganizations,
  type EpicOrganization,
} from "../api/epicApi";

interface ConnectEpicModalProps {
  open: boolean;
  onClose: () => void;
}

type Step = "picker" | "review";

const DATA_CATEGORIES = [
  "Conditions",
  "Medications",
  "Labs & Vitals",
  "Allergies & Immunizations",
  "Visit History",
  "Insurance",
  "Documents & Imaging",
];

const PINK_GRADIENT = "linear-gradient(90deg, #E8496A 0%, #F2718A 100%)";
// The app's traditional red, at its established 50%-over-white tint (same
// value as index.css's .msq-switch-dashboard-button and ConnectDataButton's
// vendor row) — matches the vendor row in ConnectDataButton's menu, so the
// same "this is Epic's own identity, not a MedSyncIQ action" mark carries
// through from the menu into this modal's header.
const EPIC_RED = "#F27F95";
// Kept separate from EPIC_RED: the trust/security note further down (the
// shield-check icon) is a reassurance message, not Epic's own branding, so
// it stays blue rather than following the vendor-identity color.
const EPIC_BLUE = "#0F5DA8";
// The platform's own brand red (index.css's --primary/--destructive) — NOT
// EPIC_RED, which is a 50%-over-white tint of this same color reserved for
// "this is Epic's own identity" marks (see EPIC_RED's comment above). The
// "Coming soon" label is a MedSyncIQ-side status, not Epic's, so it uses
// the full-strength platform red, not the Epic tint.
const PLATFORM_RED = "#E4002B";

function EpicBadge() {
  return (
    <span
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
      style={{ background: EPIC_RED }}
    >
      <Icon icon="lucide:heart-pulse" className="h-[18px] w-[18px] text-white" />
    </span>
  );
}

export function ConnectEpicModal({ open, onClose }: ConnectEpicModalProps) {
  const [step, setStep] = useState<Step>("picker");
  // Seeded from the shared cache, if a previous modal open already
  // populated it — the picker then renders this list immediately on open,
  // with no loading skeleton, while loadOrganizations() silently
  // revalidates in the background (same stale-while-revalidate behavior
  // useCachedQuery gives the admin/clinician portals; see epicApi.ts).
  const [organizations, setOrganizations] = useState<EpicOrganization[]>(
    () => getCachedEpicOrganizations() ?? [],
  );
  const [isLoading, setIsLoading] = useState(() => getCachedEpicOrganizations() === undefined);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const loadOrganizations = () => {
    const cached = getCachedEpicOrganizations();
    const hadCachedData = cached !== undefined;
    if (hadCachedData) {
      // Already have something to show — skip the skeleton entirely and
      // just quietly refresh in the background.
      setOrganizations(cached);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
    setLoadError(null);
    listEpicOrganizations()
      .then((orgs) => {
        setOrganizations(orgs);
        if (orgs.length === 0) {
          setLoadError("No hospitals are available to connect right now. Please try again shortly.");
        }
      })
      .catch((err: Error) => {
        // A background revalidation failing must never blank out a list
        // that's already on screen — only surface the error (with its own
        // Retry button) when there was nothing cached to fall back on.
        if (!hadCachedData) {
          setLoadError(err.message || "Couldn't load the list of hospitals.");
        }
      })
      .finally(() => setIsLoading(false));
  };

  // Reset to a clean first step every time the modal is (re)opened, and kick off
  // the real GET /api/epic/organizations call — never a hardcoded fallback list.
  useEffect(() => {
    if (!open) return;
    setStep("picker");
    setSelectedOrgId(null);
    setIsRedirecting(false);
    setSearch("");
    loadOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedOrg = organizations.find((o) => o.id === selectedOrgId) ?? null;

  const filteredOrganizations = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return organizations;
    return organizations.filter((org) => org.name.toLowerCase().includes(query));
  }, [organizations, search]);

  const handleContinueFromPicker = () => {
    if (!selectedOrg) return;
    setStep("review");
  };

  const handleFinalContinue = async () => {
    if (!selectedOrg || isRedirecting) return;
    setIsRedirecting(true);
    try {
      const authorizeUrl = await getEpicAuthorizeUrl(selectedOrg.id);
      // Real, full-page navigation to Epic's own OAuth screen — not intercepted
      // with fetch(), not simulated with a timeout. The page is genuinely left.
      // The spinner stays on until the browser actually leaves the page.
      window.location.href = authorizeUrl;
    } catch (err) {
      // Spinner off, then the error in the app's toast.
      setIsRedirecting(false);
      toastError(err instanceof Error && err.message ? err.message : "Couldn't start the connection. Please try again.");
    }
  };

  // Coming back with the browser's Back button can restore this page from the
  // back/forward cache with the spinner still on — reset it.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => { if (e.persisted) setIsRedirecting(false); };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  return (
    <Modal
      open={open}
      // Not closable mid-redirect (no X, no outside click, no Escape) — the browser is about to leave the page.
      onCancel={() => { if (!isRedirecting) onClose(); }}
      closable={!isRedirecting}
      maskClosable={!isRedirecting}
      keyboard={!isRedirecting}
      footer={null}
      centered
      width={480}
      className="[&_.ant-modal-content]:!relative [&_.ant-modal-content]:!overflow-hidden [&_.ant-modal-content]:!rounded-2xl [&_.ant-modal-content]:!p-0 [&_.ant-modal-body]:!p-6"
    >
      <div className="mb-5 flex items-center gap-3">
        <EpicBadge />
        <div>
          <p className="text-base font-extrabold text-[#111827]">
            {step === "picker" ? "Connect with Epic" : "Review what will be shared"}
          </p>
          <p className="text-xs text-[#6B7280]">
            {step === "picker" ? "Select the hospital you want to connect" : selectedOrg?.name}
          </p>
        </div>
      </div>

      {step === "picker" && (
        <div>
          {isLoading ? (
            <div>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="mb-2 flex items-center gap-3 rounded-xl border border-[#E5E7EB] px-4 py-3"
                >
                  <span className="h-4 w-4 shrink-0 rounded-full border border-[#E5E7EB]" />
                  <Skeleton active title={false} paragraph={{ rows: 1, width: "60%" }} className="!m-0" />
                </div>
              ))}
            </div>
          ) : loadError ? (
            <div className="py-6">
              <EmptyState
                variant="compact"
                title={loadError}
                className="!py-4"
              />
              <div className="mt-3 flex justify-center">
                <Button variant="outline" onClick={loadOrganizations} className="!rounded-full">
                  <Icon icon="lucide:refresh-cw" className="mr-1.5 h-3.5 w-3.5" />
                  Retry
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="relative mb-3">
                <Icon
                  icon="lucide:search"
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9CA3AF]"
                />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search hospitals by name..."
                  className="!w-full !rounded-xl !border-[#E5E7EB] !bg-[#F9FAFB] !py-2 !pl-9 !pr-3 !text-sm !text-[#111827] placeholder:!text-[#9CA3AF] focus:!border-[#F2718A]/50 focus:!shadow-none"
                />
              </div>

              {filteredOrganizations.length === 0 ? (
                <EmptyState
                  variant="compact"
                  title={`No hospitals match "${search}"`}
                  className="!py-8"
                />
              ) : (
                <Radio.Group
                  value={selectedOrgId}
                  onChange={(e) => setSelectedOrgId(e.target.value)}
                  className="!flex !w-full !flex-col !gap-2"
                >
                  <div className="max-h-72 overflow-y-auto pr-1">
                    {filteredOrganizations.map((org) => (
                      <label
                        key={org.id}
                        className={
                          org.reachable
                            ? "mb-2 flex cursor-pointer items-center gap-3 rounded-xl border border-[#E5E7EB] px-4 py-3 transition-colors hover:border-[#F2718A]/50 hover:bg-[#FFF5F7]"
                            : "mb-2 flex cursor-not-allowed items-center gap-3 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3"
                        }
                      >
                        <Radio value={org.id} disabled={!org.reachable} />
                        <span
                          className={
                            org.reachable
                              ? "text-sm font-semibold text-[#111827]"
                              : "text-sm font-semibold text-[#9CA3AF]"
                          }
                        >
                          {org.name}
                        </span>
                        {!org.reachable && (
                          // Plain red text — no filled pill.
                          <span className="ml-auto shrink-0 text-xs font-medium" style={{ color: PLATFORM_RED }}>
                            Coming soon
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                </Radio.Group>
              )}

              <Button
                onClick={handleContinueFromPicker}
                disabled={!selectedOrgId}
                className="!mt-4 !h-11 !w-full !rounded-full !border-none !text-sm !font-bold !text-white disabled:!opacity-50"
                style={{ background: PINK_GRADIENT }}
              >
                Continue
              </Button>
            </>
          )}
        </div>
      )}

      {step === "review" && selectedOrg && (
        <div>
          <p className="mb-3 text-xs font-bold uppercase tracking-wide text-[#9CA3AF]">
            MedSyncIQ will request access to:
          </p>
          <div className="mb-4 space-y-2 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4">
            {DATA_CATEGORIES.map((category) => (
              <Checkbox key={category} checked disabled className="!flex !items-center [&_.ant-checkbox-inner]:!bg-[#0F5DA8] [&_.ant-checkbox-inner]:!border-[#0F5DA8]">
                <span className="text-sm text-[#374151]">{category}</span>
              </Checkbox>
            ))}
          </div>

          <div className="mb-5 flex gap-2.5 rounded-xl border border-[#BFDBFE] bg-[#EFF6FF] p-3.5">
            <Icon icon="lucide:shield-check" className="mt-0.5 h-4 w-4 shrink-0" style={{ color: EPIC_BLUE }} />
            <p className="text-xs leading-5 text-[#1E3A8A]">
              You'll get a final screen from <strong>{selectedOrg.name}</strong> itself before anything is
              shared. MedSyncIQ never sees or stores your hospital portal password.
            </p>
          </div>

          <div className="flex gap-2.5">
            <Button
              variant="outline"
              onClick={() => setStep("picker")}
              disabled={isRedirecting}
              className="!h-11 !rounded-full !px-5"
            >
              Back
            </Button>
            <Button
              onClick={handleFinalContinue}
              disabled={isRedirecting}
              aria-busy={isRedirecting}
              className="!h-11 !flex-1 !rounded-full !border-none !text-sm !font-bold !text-white disabled:!opacity-50"
              style={{ background: PINK_GRADIENT }}
            >
              Continue to Epic
            </Button>
          </div>
        </div>
      )}

      {/* While redirecting: the modal blurs under the platform's red spinner, centred,
          and nothing in it can be clicked until the browser leaves for the hospital. */}
      {isRedirecting && (
        <div
          role="status"
          aria-label="Redirecting to your hospital"
          data-testid="epic-redirect-spinner"
          className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/50 backdrop-blur-[2px]"
        >
          <Spinner />
        </div>
      )}
    </Modal>
  );
}
